/**
 * AI Studio panel — opens from a content card and lets the team generate:
 *   - Hooks
 *   - Full script
 *   - SEO bundle (titles, description, tags, hashtags, chapters)
 *   - Thumbnail brief
 *
 * Output is editable, then can be saved back to the content item.
 */
import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  X, Wand2, Loader2, Save, ScrollText, Hash, Image as ImageIcon, Zap, Check,
  Presentation, Download,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || '';

const TABS = [
  { key: "hooks", label: "Hooks", icon: Zap },
  { key: "script", label: "Script", icon: ScrollText },
  { key: "seo", label: "SEO bundle", icon: Hash },
  { key: "thumb", label: "Thumb brief", icon: ImageIcon },
];

export default function StudioAIPanel({ open, onClose, channelId, contentItem, onSaved }) {
  const [tab, setTab] = useState("hooks");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  if (!open || !contentItem) return null;

  const run = async () => {
    setBusy(true);
    setOut(null);
    try {
      let url = "", body = { channel_id: channelId, title: contentItem.title };
      if (tab === "hooks") { url = "/api/studio/ai/hooks"; body.count = 5; }
      if (tab === "script") { url = "/api/studio/ai/script"; body.duration_min = 8; body.language = "english"; }
      if (tab === "seo") { url = "/api/studio/ai/seo"; if (contentItem.script) body.script = contentItem.script; }
      if (tab === "thumb") { url = "/api/studio/ai/thumbnail-brief"; }
      const r = await axios.post(`${API}${url}`, body, { withCredentials: true });
      setOut(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI request failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async (kind, payload) => {
    try {
      await axios.post(`${API}/api/studio/ai/save`, {
        content_id: contentItem._id,
        kind,
        payload,
      }, { withCredentials: true });
      toast.success(`${kind} saved to the video`);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  return (
    <div className="studio-ai-overlay" onClick={onClose} data-testid="ai-panel">
      <motion.div
        className="studio-ai-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <header>
          <div>
            <span className="studio-eyebrow">
              <Wand2 size={12}/> AI Studio
            </span>
            <h2>{contentItem.title}</h2>
          </div>
          <button className="studio-ai-close" onClick={onClose} data-testid="ai-close"><X size={16}/></button>
        </header>

        <div className="studio-tabs" role="tablist">
          {TABS.map((t) => {
            const I = t.icon;
            return (
              <button
                key={t.key}
                role="tab"
                className="studio-tab"
                data-active={tab === t.key}
                onClick={() => { setTab(t.key); setOut(null); }}
                data-testid={`ai-tab-${t.key}`}
              >
                <I size={14}/> {t.label}
              </button>
            );
          })}
        </div>

        <div className="studio-ai-body">
          {!out && tab === "script" && contentItem.script && (
            <div className="studio-ai-script">
              <p className="studio-muted" style={{ marginTop: 0 }}>
                Saved script (last generated). Edit, or click <strong>Generate</strong> for a fresh one.
              </p>
              <textarea
                className="studio-ai-script-text"
                value={contentItem.script}
                readOnly
                data-testid="ai-script-saved"
              />
              <div className="studio-form-actions">
                <button className="studio-btn-ghost" onClick={run} disabled={busy} data-testid="ai-regen-saved">
                  {busy ? <Loader2 size={14} className="ac-spin"/> : <Wand2 size={14}/>} Regenerate
                </button>
                <button
                  className="studio-btn-primary"
                  onClick={async () => {
                    setBusy(true);
                    try {
                      const r = await axios.post(`${API}/api/studio/ai/ppt`, {
                        content_id: contentItem._id,
                      }, { withCredentials: true });
                      toast.success("PPT generated");
                      window.open(`${API}${r.data.url}`, "_blank");
                      onSaved && onSaved();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "PPT generation failed");
                    } finally { setBusy(false); }
                  }}
                  disabled={busy}
                  data-testid="ai-gen-ppt-saved"
                >
                  {busy ? <Loader2 size={14} className="ac-spin"/> : <Presentation size={14}/>} Generate PPT
                </button>
              </div>
            </div>
          )}

          {!out && !(tab === "script" && contentItem.script) && (
            <div className="studio-ai-cta">
              <p>Click <strong>Generate</strong> — Claude Sonnet will use this channel&apos;s niche, voice and tone to craft on-brand {TABS.find((t) => t.key === tab)?.label.toLowerCase()}.</p>
              <button className="studio-btn-primary" onClick={run} disabled={busy} data-testid="ai-run">
                {busy ? <Loader2 size={14} className="ac-spin"/> : <Wand2 size={14}/>} Generate
              </button>
            </div>
          )}

          {out && tab === "hooks" && (
            <div className="studio-ai-hooks">
              {(out.hooks || []).map((h, i) => (
                <div key={i} className="studio-ai-hook" data-testid={`ai-hook-${i}`}>
                  <span className="studio-pill studio-pill--sm">{h.style}</span>
                  <p>{h.hook}</p>
                </div>
              ))}
              <button className="studio-btn-ghost" onClick={run}>Regenerate</button>
            </div>
          )}

          {out && tab === "script" && (
            <div className="studio-ai-script">
              <textarea
                className="studio-ai-script-text"
                value={out.script_markdown || ""}
                onChange={(e) => setOut({ ...out, script_markdown: e.target.value })}
                data-testid="ai-script-text"
              />
              <div className="studio-form-actions">
                <button className="studio-btn-ghost" onClick={run}>Regenerate</button>
                <button className="studio-btn-primary" onClick={() => save("script", out.script_markdown)} data-testid="ai-save-script">
                  <Save size={14}/> Save script
                </button>
                <button
                  className="studio-btn-ghost"
                  onClick={async () => {
                    if (!contentItem.script && !out.script_markdown) {
                      toast.error("Save the script first."); return;
                    }
                    setBusy(true);
                    try {
                      // ensure script is persisted before PPT
                      if (!contentItem.script) {
                        await axios.post(`${API}/api/studio/ai/save`, {
                          content_id: contentItem._id, kind: "script", payload: out.script_markdown,
                        }, { withCredentials: true });
                      }
                      const r = await axios.post(`${API}/api/studio/ai/ppt`, {
                        content_id: contentItem._id,
                      }, { withCredentials: true });
                      toast.success("PPT generated");
                      window.open(`${API}${r.data.url}`, "_blank");
                      onSaved && onSaved();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "PPT generation failed");
                    } finally { setBusy(false); }
                  }}
                  disabled={busy}
                  data-testid="ai-gen-ppt"
                >
                  {busy ? <Loader2 size={14} className="ac-spin"/> : <Presentation size={14}/>} Generate PPT
                </button>
              </div>
            </div>
          )}

          {out && tab === "seo" && (
            <div className="studio-ai-seo">
              <h4>Titles</h4>
              <ul>{(out.titles || []).map((t, i) => <li key={i}>{t}</li>)}</ul>
              <h4>Description</h4>
              <pre>{out.description}</pre>
              <h4>Tags</h4>
              <div className="studio-tag-row">{(out.tags || []).map((t, i) => <span key={i} className="studio-pill studio-pill--sm">{t}</span>)}</div>
              <h4>Hashtags</h4>
              <div className="studio-tag-row">{(out.hashtags || []).map((t, i) => <span key={i} className="studio-pill studio-pill--sm">{t}</span>)}</div>
              <h4>Chapters</h4>
              <ul>{(out.chapters || []).map((c, i) => <li key={i}><code>{c.time}</code> {c.label}</li>)}</ul>
              <h4>Pinned comment</h4>
              <p>{out.pinned_comment}</p>
              <h4>End-screen suggestion</h4>
              <p>{out.end_screen}</p>
              <div className="studio-form-actions">
                <button className="studio-btn-ghost" onClick={run}>Regenerate</button>
                <button className="studio-btn-primary" onClick={() => save("seo", out)} data-testid="ai-save-seo">
                  <Save size={14}/> Save SEO bundle
                </button>
              </div>
            </div>
          )}

          {out && tab === "thumb" && (
            <div className="studio-ai-thumb">
              <h4>Concept</h4>
              <p>{out.concept}</p>
              <div className="studio-form-row">
                <div><strong>Background</strong><p>{out.background}</p></div>
                <div><strong>Emotion</strong><p>{out.emotion}</p></div>
              </div>
              <h4>Text overlay</h4>
              <p style={{ fontSize: "1.5rem", fontWeight: 800, color: out.text_color || "#fff" }}>
                {out.text_overlay} {out.text_color && <span style={{ fontSize: "0.7rem", color: "#888" }}>({out.text_color})</span>}
              </p>
              <h4>Pose</h4>
              <p>{out.pose}</p>
              <h4>Objects</h4>
              <div className="studio-tag-row">{(out.objects || []).map((o, i) => <span key={i} className="studio-pill studio-pill--sm">{o}</span>)}</div>
              <h4>Composition</h4>
              <p>{out.composition}</p>
              <h4>CTR tips</h4>
              <ul>{(out.ctr_tips || []).map((c, i) => <li key={i}><Check size={12}/> {c}</li>)}</ul>
              <div className="studio-form-actions">
                <button className="studio-btn-ghost" onClick={run}>Regenerate</button>
                <button className="studio-btn-primary" onClick={() => save("thumbnail_brief", out)} data-testid="ai-save-thumb">
                  <Save size={14}/> Save brief
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
